import { useState } from "react";
import { useUsers, useDeleteUser, useApproveUser, type User } from "../api/users";
import { UserForm } from "./UserForm";
import { GuestMembershipsPanel } from "./GuestMembershipsPanel";
import { TotpStatusPanel } from "./TotpStatusPanel";
import "./users-panel.css";

export const UsersPanel = (): JSX.Element => {
    const [search, setSearch] = useState("");
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [showUserForm, setShowUserForm] = useState(false);
    const [showGuestMembershipsPanel, setShowGuestMembershipsPanel] = useState(false);
    const [showTotpStatusPanel, setShowTotpStatusPanel] = useState(false);
    const { data: users = [], isLoading, error } = useUsers({
        search: search || undefined,
    });

    const deleteMutation = useDeleteUser();
    const approveMutation = useApproveUser();

    const handleEdit = (user: User) => {
        setSelectedUser(user);
        setShowUserForm(true);
    };

    const handleDelete = async (user: User) => {
        if (confirm(`Är du säker på att du vill inaktivera användaren ${user.userName}?`)) {
            try {
                await deleteMutation.mutateAsync(user.id);
            } catch (error) {
                alert("Kunde inte ta bort användaren");
            }
        }
    };

    const handleApprove = async (user: User) => {
        try {
            await approveMutation.mutateAsync(user.id);
        } catch (error) {
            alert("Kunde inte godkänna användaren");
        }
    };

    const handleManageGuestMemberships = (user: User) => {
        setSelectedUser(user);
        setShowGuestMembershipsPanel(true);
    };

    const handleManageTotpStatus = (user: User) => {
        setSelectedUser(user);
        setShowTotpStatusPanel(true);
    };

    if (error) {
        return (
            <div className="users-panel">
                <h2>Användare</h2>
                <p className="error-message">Kunde inte ladda användare.</p>
            </div>
        );
    }

    return (
        <>
            <section className="users-panel" aria-label="Användare">
                <div className="users-panel__header">
                    <h2>Användare</h2>
                    <button
                        className="users-panel__add-button"
                        onClick={() => {
                            setSelectedUser(null);
                            setShowUserForm(true);
                        }}
                    >
                        + Lägg till användare
                    </button>
                </div>
                <div className="users-panel__search">
                    <input
                        type="text"
                        placeholder="Sök användare..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="search-input"
                        aria-label="Sök användare"
                    />
                </div>
                {isLoading ? (
                    <p>Laddar...</p>
                ) : (
                    <div className="users-panel__list">
                        {users.length === 0 ? (
                            <p>Inga användare hittades.</p>
                        ) : (
                            <table className="users-table">
                                <thead>
                                    <tr>
                                        <th>Användarnamn</th>
                                        <th>E-post</th>
                                        <th>Team</th>
                                        <th>Status</th>
                                        <th>Senaste inloggning</th>
                                        <th>Åtgärder</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map((user) => (
                                        <tr key={user.id}>
                                            <td>{user.userName}</td>
                                            <td>{user.email}</td>
                                            <td>{user.primaryTeamName || "-"}</td>
                                            <td>
                                                <span className={`status-badge ${user.isApproved ? "approved" : "pending"}`}>
                                                    {user.isApproved ? "Godkänd" : "Väntande"}
                                                </span>
                                            </td>
                                            <td>
                                                {user.lastLoginAt
                                                    ? new Date(user.lastLoginAt).toLocaleDateString("sv-SE")
                                                    : "-"}
                                            </td>
                                            <td>
                                                <div className="users-table__actions">
                                                    <button
                                                        className="action-button"
                                                        onClick={() => handleEdit(user)}
                                                        title="Redigera"
                                                    >
                                                        Redigera
                                                    </button>
                                                    <button
                                                        className="action-button"
                                                        onClick={() => handleEdit(user)}
                                                        title="Hantera roller"
                                                    >
                                                        Roller
                                                    </button>
                                                    <button
                                                        className="action-button"
                                                        onClick={() => handleManageGuestMemberships(user)}
                                                        title="Hantera gästmedlemskap"
                                                    >
                                                        Gäster
                                                    </button>
                                                    <button
                                                        className="action-button"
                                                        onClick={() => handleManageTotpStatus(user)}
                                                        title="TOTP Status"
                                                    >
                                                        TOTP
                                                    </button>
                                                    {!user.isApproved && (
                                                        <button
                                                            className="action-button action-button--approve"
                                                            onClick={() => handleApprove(user)}
                                                            title="Godkänn"
                                                        >
                                                            Godkänn
                                                        </button>
                                                    )}
                                                    <button
                                                        className="action-button action-button--delete"
                                                        onClick={() => handleDelete(user)}
                                                        title="Ta bort"
                                                    >
                                                        Ta bort
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}
            </section>

            {showUserForm && (
                <UserForm
                    user={selectedUser ?? undefined}
                    onSuccess={() => {
                        setShowUserForm(false);
                        setSelectedUser(null);
                    }}
                    onCancel={() => {
                        setShowUserForm(false);
                        setSelectedUser(null);
                    }}
                />
            )}

            {showGuestMembershipsPanel && selectedUser && (
                <GuestMembershipsPanel
                    userId={selectedUser.id}
                    userName={selectedUser.userName}
                    onClose={() => {
                        setShowGuestMembershipsPanel(false);
                        setSelectedUser(null);
                    }}
                />
            )}

            {showTotpStatusPanel && selectedUser && (
                <TotpStatusPanel
                    userId={selectedUser.id}
                    userName={selectedUser.userName}
                    onClose={() => {
                        setShowTotpStatusPanel(false);
                        setSelectedUser(null);
                    }}
                />
            )}
        </>
    );
};